import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { SignaturePad } from '@/components/publicEstimate/SignaturePad';
import {
  exportSignaturePng,
  hasInk,
  TYPED_SIGNATURE_CLASS,
  type ExportProblem,
  type Stroke,
} from '@/lib/signaturePad';
import { cn } from '@/lib/utils';
import {
  newSubmissionId,
  PublicAnswerInvalidError,
  PublicAnswerRefusedError,
  PublicEstimateGoneError,
  type PublicConsent,
  type usePublicAnswer,
} from '@/routes/publicEstimate/usePublicEstimate';

/** `EstimateAnswer.CommentMaxLength` (= the estimate's note limit), measured after trimming. */
export const DECLINE_COMMENT_MAX_LENGTH = 2000;

type Mode = 'choose' | 'approve' | 'decline';
type Kind = 'Drawn' | 'Typed';
type Field = 'name' | 'signature' | 'consent';
type FieldErrors = Partial<Record<Field, string>>;

/** Which of the form's fields each of the API's 400 keys belongs to. */
const API_FIELD: Record<string, Field> = {
  SignerName: 'name',
  SignatureKind: 'signature',
  SignatureImage: 'signature',
  TypedSignature: 'signature',
  Consented: 'consent',
  ConsentVersion: 'consent',
};

const INPUT_CLASS =
  'h-11 w-full rounded-md border bg-transparent px-3 text-base focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';
const TEXTAREA_CLASS =
  'w-full rounded-md border bg-transparent px-3 py-2 text-base focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

/**
 * The customer's answer on an Open link (M2-08, pass B): approve and sign, or
 * decline with an optional comment. Mobile-first — one column, full-width
 * controls at the 44px floor, text at 16px so iOS does not zoom into a field.
 *
 * <b>Approve</b> takes the typed full name, a signature (drawn on the pad, or
 * typed — the keyboard path), and the consent line ticked in the API's own
 * wording and version (`consent`). <b>Decline</b> is a second step with an
 * optional comment; neither answer is one tap from the quote.
 *
 * ⚠️ <b>One `submissionId` per OUTCOME, for as long as the panel is on the
 * page</b> (Kyle, 2026-10-01). Approve and decline each draw theirs once, and
 * every send of that outcome carries it — a retry, or the step reopened after
 * Go back. So an approval whose response was lost on a slow phone and is sent
 * again, by either path, is a replay the API answers 200 with the page, never a
 * second record or a 409 against the customer's own answer. The two ids are
 * different, as they must be: the API's replay is the same id AND the same
 * outcome, so one id never travels with both. A fresh load gets new ids. Every
 * control is disabled while the answer is in flight.
 *
 * Where each refusal is shown: a 400 under the field it names (the API's
 * sentence); a 409 at the top of the page, by the caller, because the page
 * behind it is re-read and may no longer be this form at all; a failure with
 * no sentence (the network) beside the button, to try again.
 */
export function AnswerPanel({
  consent,
  answer,
}: {
  consent: PublicConsent;
  answer: ReturnType<typeof usePublicAnswer>;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [mode, setMode] = useState<Mode>('choose');
  const [submissionIds] = useState(() => ({
    approve: newSubmissionId(),
    decline: newSubmissionId(),
  }));
  const approveButton = useRef<HTMLButtonElement>(null);
  const declineButton = useRef<HTMLButtonElement>(null);
  const returnFocusTo = useRef<'approve' | 'decline' | null>(null);

  useEffect(() => {
    if (mode !== 'choose' || returnFocusTo.current === null) return;
    (returnFocusTo.current === 'approve' ? approveButton : declineButton).current?.focus();
    returnFocusTo.current = null;
  }, [mode]);

  function open(next: 'approve' | 'decline') {
    answer.approve.reset();
    answer.decline.reset();
    setMode(next);
  }

  function back(from: 'approve' | 'decline') {
    returnFocusTo.current = from;
    setMode('choose');
  }

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-4 rounded-lg border border-[var(--color-border)] p-4"
      data-testid="answer-panel"
    >
      {mode === 'choose' ? (
        <>
          <h2 id={`${id}-heading`} className="text-base font-semibold">
            {t('publicEstimate.answer.heading')}
          </h2>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('publicEstimate.answer.intro')}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              ref={approveButton}
              type="button"
              className="h-11 w-full text-base sm:w-auto"
              onClick={() => open('approve')}
            >
              {t('publicEstimate.answer.approve')}
            </Button>
            <Button
              ref={declineButton}
              type="button"
              variant="outline"
              className="h-11 w-full text-base sm:w-auto"
              onClick={() => open('decline')}
            >
              {t('publicEstimate.answer.decline')}
            </Button>
          </div>
        </>
      ) : mode === 'approve' ? (
        <ApproveForm
          headingId={`${id}-heading`}
          consent={consent}
          answer={answer}
          submissionId={submissionIds.approve}
          onBack={() => back('approve')}
        />
      ) : (
        <DeclineForm
          headingId={`${id}-heading`}
          answer={answer}
          submissionId={submissionIds.decline}
          onBack={() => back('decline')}
        />
      )}
    </section>
  );
}

/** A step's heading takes focus when the step opens, so a screen reader starts there. */
function StepHeading({ id, children }: { id: string; children: ReactNode }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => ref.current?.focus(), []);
  return (
    <h2 id={id} ref={ref} tabIndex={-1} className="text-base font-semibold focus:outline-none">
      {children}
    </h2>
  );
}

function ApproveForm({
  headingId,
  consent,
  answer,
  submissionId,
  onBack,
}: {
  headingId: string;
  consent: PublicConsent;
  answer: ReturnType<typeof usePublicAnswer>;
  submissionId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<Kind>('Drawn');
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [typed, setTyped] = useState('');
  // The VERSION ticked, not a boolean: if the page is re-read with different
  // wording (the API refused the old version), the box is no longer ticked
  // for words the customer has not seen.
  const [consentedTo, setConsentedTo] = useState<string | null>(null);
  const [clientErrors, setClientErrors] = useState<FieldErrors>({});
  const [exporting, setExporting] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const drawnRef = useRef<HTMLInputElement>(null);
  const typedRadioRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const consented = consentedTo === consent.version;
  const pending = exporting || answer.approve.isPending;
  const error = answer.approve.error;
  const serverErrors: FieldErrors = {};
  let general: string | null = null;
  if (error instanceof PublicAnswerInvalidError) {
    for (const [key, sentences] of Object.entries(error.errors)) {
      const field = API_FIELD[key];
      if (field && !serverErrors[field]) serverErrors[field] = sentences[0];
      else if (!field) general ??= sentences[0] ?? null;
    }
  } else if (
    error &&
    !(error instanceof PublicAnswerRefusedError) &&
    !(error instanceof PublicEstimateGoneError)
  ) {
    general = t('publicEstimate.answer.sendFailed');
  }
  const errors: FieldErrors = { ...serverErrors, ...clientErrors };

  // A 400 arrives after the button was pressed: say so, and put the customer
  // on the field it is about.
  useEffect(() => {
    if (!(error instanceof PublicAnswerInvalidError)) return;
    const first = Object.keys(error.errors)
      .map((key) => API_FIELD[key])
      .find(Boolean);
    setAnnouncement(error.message);
    if (first) focusField(first);
    // Only when a new refusal arrives (`focusField` reads the current kind).
  }, [error]);

  useEffect(() => {
    if (!general) return;
    setAnnouncement(general);
    restoreFocus(submitRef.current);
  }, [general]);

  function focusField(field: Field) {
    if (field === 'name') nameRef.current?.focus();
    // The pad is not focusable; its method's radio carries the error instead.
    else if (field === 'signature') (kind === 'Drawn' ? drawnRef : typedRadioRef).current?.focus();
    else consentRef.current?.focus();
  }

  function validate(): FieldErrors {
    const found: FieldErrors = {};
    if (!name.trim()) found.name = t('publicEstimate.answer.nameRequired');
    if (kind === 'Drawn' && !hasInk(strokes)) {
      found.signature = t('publicEstimate.answer.drawRequired');
    }
    if (kind === 'Typed' && !typed.trim()) {
      found.signature = t('publicEstimate.answer.typedRequired');
    }
    if (!consented)
      found.consent = t('publicEstimate.answer.consentRequired', { text: consent.text });
    return found;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    // A new try: the last refusal's sentences go, so one about a field since
    // corrected is not left on it when this try is stopped short by the page.
    answer.approve.reset();
    const found = validate();
    let image: string | null = null;
    if (!found.signature && kind === 'Drawn') {
      setExporting(true);
      const exported = canvasRef.current
        ? await exportSignaturePng(canvasRef.current, strokes)
        : ({ ok: false, problem: 'failed' } as const);
      setExporting(false);
      if (exported.ok) image = exported.base64;
      else found.signature = exportSentence(exported.problem);
    }
    setClientErrors(found);
    const first = (['name', 'signature', 'consent'] as const).find((f) => found[f]);
    if (first) {
      setAnnouncement(found[first]!);
      focusField(first);
      return;
    }
    setAnnouncement('');
    answer.approve.mutate({
      submissionId,
      signerName: name.trim(),
      signatureKind: kind,
      consented: true,
      consentVersion: consent.version,
      ...(kind === 'Drawn' ? { signatureImage: image } : { typedSignature: typed.trim() }),
    });
  }

  function exportSentence(problem: ExportProblem): string {
    switch (problem) {
      case 'empty':
        return t('publicEstimate.answer.drawRequired');
      case 'tooLarge':
        return t('publicEstimate.answer.drawTooLarge');
      default:
        return t('publicEstimate.answer.drawFailed');
    }
  }

  /** Editing a field clears ITS client error; the server's stay until the next try (`onSubmit`). */
  function clear(field: Field) {
    setClientErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  const describe = (...ids: (string | false | undefined)[]) =>
    ids.filter(Boolean).join(' ') || undefined;

  return (
    <form noValidate onSubmit={(event) => void onSubmit(event)} className="space-y-5">
      <div className="space-y-1">
        <StepHeading id={headingId}>{t('publicEstimate.answer.approveHeading')}</StepHeading>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('publicEstimate.answer.approveIntro')}
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor={`${id}-name`} className="block text-sm font-medium">
          {t('publicEstimate.answer.name')}
        </label>
        <input
          ref={nameRef}
          id={`${id}-name`}
          type="text"
          autoComplete="name"
          className={cn(INPUT_CLASS, borderFor(errors.name))}
          value={name}
          disabled={pending}
          aria-invalid={errors.name ? true : undefined}
          aria-describedby={describe(errors.name && `${id}-name-error`)}
          onChange={(event) => {
            setName(event.target.value);
            clear('name');
          }}
        />
        <ErrorText id={`${id}-name-error`} message={errors.name} />
      </div>

      <fieldset
        className="space-y-3"
        aria-describedby={describe(errors.signature && `${id}-signature-error`)}
      >
        <legend id={`${id}-signature-legend`} className="text-sm font-medium">
          {t('publicEstimate.answer.signature')}
        </legend>
        <div className="flex gap-6">
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              ref={drawnRef}
              type="radio"
              name={`${id}-kind`}
              className="h-5 w-5"
              checked={kind === 'Drawn'}
              disabled={pending}
              aria-invalid={errors.signature && kind === 'Drawn' ? true : undefined}
              aria-describedby={describe(errors.signature && `${id}-signature-error`)}
              onChange={() => {
                setKind('Drawn');
                clear('signature');
              }}
            />
            {t('publicEstimate.answer.draw')}
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              ref={typedRadioRef}
              type="radio"
              name={`${id}-kind`}
              className="h-5 w-5"
              checked={kind === 'Typed'}
              disabled={pending}
              aria-invalid={errors.signature && kind === 'Typed' ? true : undefined}
              aria-describedby={describe(errors.signature && `${id}-signature-error`)}
              onChange={() => {
                setKind('Typed');
                clear('signature');
              }}
            />
            {t('publicEstimate.answer.type')}
          </label>
        </div>

        {kind === 'Drawn' ? (
          <div className="space-y-1">
            <p id={`${id}-pad-label`} className="text-sm">
              {t('publicEstimate.answer.padLabel')}
            </p>
            <SignaturePad
              strokes={strokes}
              onStrokesChange={(next) => {
                setStrokes(next);
                clear('signature');
              }}
              canvasRef={canvasRef}
              labelId={`${id}-pad-label`}
              describedBy={describe(`${id}-pad-hint`, errors.signature && `${id}-signature-error`)}
              invalid={Boolean(errors.signature)}
              disabled={pending}
            />
            <p id={`${id}-pad-hint`} className="text-xs text-[var(--color-muted-foreground)]">
              {t('publicEstimate.answer.padHint')}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <label htmlFor={`${id}-typed`} className="block text-sm">
              {t('publicEstimate.answer.typedLabel')}
            </label>
            <input
              id={`${id}-typed`}
              type="text"
              autoComplete="off"
              className={cn(INPUT_CLASS, borderFor(errors.signature))}
              value={typed}
              disabled={pending}
              aria-invalid={errors.signature ? true : undefined}
              aria-describedby={describe(errors.signature && `${id}-signature-error`)}
              onChange={(event) => {
                setTyped(event.target.value);
                clear('signature');
              }}
            />
            {typed.trim() ? (
              <p
                className={cn(TYPED_SIGNATURE_CLASS, 'break-words')}
                aria-hidden="true"
                data-testid="typed-preview"
              >
                {typed.trim()}
              </p>
            ) : null}
          </div>
        )}
        <ErrorText id={`${id}-signature-error`} message={errors.signature} />
      </fieldset>

      <div className="space-y-1">
        <label className="flex items-start gap-3 text-sm">
          <input
            ref={consentRef}
            type="checkbox"
            className="mt-0.5 h-5 w-5 shrink-0"
            checked={consented}
            disabled={pending}
            aria-invalid={errors.consent ? true : undefined}
            aria-describedby={describe(errors.consent && `${id}-consent-error`)}
            onChange={(event) => {
              setConsentedTo(event.target.checked ? consent.version : null);
              clear('consent');
            }}
          />
          <span data-testid="consent-text">{consent.text}</span>
        </label>
        <ErrorText id={`${id}-consent-error`} message={errors.consent} />
      </div>

      {general ? (
        <p
          id={`${id}-general-error`}
          className="text-sm text-[var(--color-destructive)]"
          data-testid="answer-general-error"
        >
          {general}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          ref={submitRef}
          type="submit"
          className="h-11 w-full text-base sm:w-auto"
          disabled={pending}
          aria-busy={pending || undefined}
          aria-describedby={general ? `${id}-general-error` : undefined}
        >
          {pending ? t('publicEstimate.answer.sending') : t('publicEstimate.answer.approveSubmit')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full text-base sm:w-auto"
          disabled={pending}
          onClick={onBack}
        >
          {t('publicEstimate.answer.back')}
        </Button>
      </div>

      <p role="alert" className="sr-only">
        {announcement}
      </p>
    </form>
  );
}

function DeclineForm({
  headingId,
  answer,
  submissionId,
  onBack,
}: {
  headingId: string;
  answer: ReturnType<typeof usePublicAnswer>;
  submissionId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [comment, setComment] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const used = comment.trim().length;
  const over = used > DECLINE_COMMENT_MAX_LENGTH;
  const pending = answer.decline.isPending;
  const error = answer.decline.error;

  let fieldError = clientError;
  let general: string | null = null;
  if (error instanceof PublicAnswerInvalidError) {
    if (error.errors.Comment) fieldError ??= error.errors.Comment[0] ?? null;
    else general = error.message;
  } else if (
    error &&
    !(error instanceof PublicAnswerRefusedError) &&
    !(error instanceof PublicEstimateGoneError)
  ) {
    general = t('publicEstimate.answer.sendFailed');
  }

  useEffect(() => {
    if (error instanceof PublicAnswerInvalidError) {
      setAnnouncement(error.message);
      if (error.errors.Comment) commentRef.current?.focus();
    }
  }, [error]);

  useEffect(() => {
    if (!general) return;
    setAnnouncement(general);
    restoreFocus(submitRef.current);
  }, [general]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (pending) return;
    answer.decline.reset();
    if (over) {
      const sentence = t('publicEstimate.answer.commentTooLong', {
        max: DECLINE_COMMENT_MAX_LENGTH,
      });
      setClientError(sentence);
      setAnnouncement(sentence);
      commentRef.current?.focus();
      return;
    }
    setAnnouncement('');
    // Blank is no comment, as the API stores it.
    answer.decline.mutate({ submissionId, comment: comment.trim() || null });
  }

  return (
    <form noValidate onSubmit={onSubmit} className="space-y-5">
      <div className="space-y-1">
        <StepHeading id={headingId}>{t('publicEstimate.answer.declineHeading')}</StepHeading>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('publicEstimate.answer.declineIntro')}
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor={`${id}-comment`} className="block text-sm font-medium">
          {t('publicEstimate.answer.comment')}
        </label>
        {/* No maxLength: a paste over the limit would be cut without a word
            (M2-07's notes). The count turns red and the send is held instead. */}
        <textarea
          ref={commentRef}
          id={`${id}-comment`}
          rows={4}
          className={cn(TEXTAREA_CLASS, borderFor(fieldError ?? (over ? 'over' : undefined)))}
          value={comment}
          disabled={pending}
          aria-invalid={fieldError || over ? true : undefined}
          aria-describedby={[`${id}-count`, fieldError ? `${id}-comment-error` : null]
            .filter(Boolean)
            .join(' ')}
          onChange={(event) => {
            setComment(event.target.value);
            setClientError(null);
          }}
        />
        <p
          id={`${id}-count`}
          className={cn(
            'text-right text-xs tabular-nums',
            over ? 'text-[var(--color-destructive)]' : 'text-[var(--color-muted-foreground)]',
          )}
          data-testid="comment-count"
        >
          {t('publicEstimate.answer.commentCount', { used, max: DECLINE_COMMENT_MAX_LENGTH })}
        </p>
        <ErrorText id={`${id}-comment-error`} message={fieldError ?? undefined} />
      </div>

      {general ? (
        <p
          id={`${id}-general-error`}
          className="text-sm text-[var(--color-destructive)]"
          data-testid="answer-general-error"
        >
          {general}
        </p>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          ref={submitRef}
          type="submit"
          variant="destructive"
          className="h-11 w-full text-base sm:w-auto"
          disabled={pending}
          aria-busy={pending || undefined}
          aria-describedby={general ? `${id}-general-error` : undefined}
        >
          {pending ? t('publicEstimate.answer.sending') : t('publicEstimate.answer.declineSubmit')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 w-full text-base sm:w-auto"
          disabled={pending}
          onClick={onBack}
        >
          {t('publicEstimate.answer.back')}
        </Button>
      </div>

      <p role="alert" className="sr-only">
        {announcement}
      </p>
    </form>
  );
}

/**
 * Puts focus back on `target` after a send that failed with no field to point
 * at — but only if focus was DROPPED. A browser blurs the submit button when
 * it is disabled for the send (seen in Chromium: focus is on the body when the
 * failure lands), which would leave a keyboard or screen-reader user nowhere;
 * the button is where trying again starts. Focus a 400 already put on a field
 * is left there.
 */
function restoreFocus(target: HTMLElement | null) {
  const active = document.activeElement;
  if (active === null || active === document.body) target?.focus();
}

function borderFor(error: string | undefined | null): string {
  return error ? 'border-[var(--color-destructive)]' : 'border-[var(--color-input)]';
}

function ErrorText({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="text-sm text-[var(--color-destructive)]">
      {message}
    </p>
  );
}
